import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  type ReactNode,
  type CSSProperties,
} from 'react';
import { useRouter } from '@tanstack/react-router';
import { useAdaptive } from '@/lib/responsive';

export interface ResponsiveOverlayProps {
  open?: boolean;
  onClose: () => void | boolean;
  kind?: 'detail' | 'form' | 'picker' | 'confirmation' | 'progress';
  dismissible?: boolean;
  label: string;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}
const stack: string[] = [];
// A history.back() an overlay's cleanup issued and whose popstate has not
// landed yet. React re-runs a layout effect without unmounting (StrictMode's
// deferred replay after a concurrent commit, a Suspense hide and show), and one
// overlay can open in the tick another closes; either way a pushState would
// race the still-travelling back, whose popstate then lands on the fresh entry
// and reads as the user leaving. A mount waits for that popstate first.
let pendingBack: Promise<void> | null = null;
const scrollLocks = new Set<symbol>();
let bodyOverflow = '';
// Safari does not focus buttons on a pointer click. Remember the actual
// invoker before showModal() moves focus, including nested dialog openers.
let pointerInvoker: { node: HTMLElement; at: number } | null = null;
const rememberInvoker = (event: PointerEvent) => {
  const node =
    event.target instanceof Element
      ? event.target.closest<HTMLElement>('button,a,input,select,textarea,[role=button],[tabindex]')
      : null;
  pointerInvoker = node ? { node, at: performance.now() } : null;
};
const keyboardInvocation = () => {
  pointerInvoker = null;
};
if (typeof document !== 'undefined') {
  document.addEventListener('pointerdown', rememberInvoker, true);
  document.addEventListener('keydown', keyboardInvocation, true);
  import.meta.hot?.dispose(() => {
    document.removeEventListener('pointerdown', rememberInvoker, true);
    document.removeEventListener('keydown', keyboardInvocation, true);
  });
}

/**
 * Run `then`, a navigation away from the screen behind the overlay, only once the overlay's
 * own history entry is gone.
 *
 * On a touch layout the overlay pushes an entry and its cleanup pops it with an asynchronous
 * `history.back()`; a navigation started before that Back lands is undone by it (a drawer's
 * Duplicate on a phone navigated to the wizard and came straight back). In-app links inside the
 * overlay get this for free (the capture handler below); a button that navigates in code calls
 * this. Outside an overlay, or with no entry, `then` runs at once.
 */
const OverlayLeaveContext = createContext<((then: () => void) => void) | null>(null);

export function useLeaveOverlay(): (then: () => void) => void {
  return useContext(OverlayLeaveContext) ?? runNow;
}

const runNow = (then: () => void) => then();

/** A button inside an overlay whose action navigates away (Done · go to campaigns, Launch
 *  automatically when ready): it leaves the overlay's history entry first, as
 *  `useLeaveOverlay` does, so a phone's closing Back cannot undo the navigation. */
export function OverlayLeaveButton({ onLeave, className, disabled, children }: {
  onLeave: () => void;
  className?: string;
  disabled?: boolean;
  children: ReactNode;
}) {
  const leave = useLeaveOverlay();
  return (
    <button className={className} type="button" disabled={disabled} onClick={() => leave(onLeave)}>
      {children}
    </button>
  );
}

/** Native modal focus/inertness, shared across existing screen presentations. */
export function ResponsiveOverlay({
  open = true,
  onClose,
  kind = 'detail',
  dismissible = true,
  label,
  className = '',
  style,
  children,
}: ResponsiveOverlayProps) {
  const router = useRouter({ warn: false });
  const ref = useRef<HTMLDialogElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const dismissRef = useRef(dismissible);
  dismissRef.current = dismissible;
  // Only touch layouts get a history entry per overlay (hardware/gesture Back
  // closes the sheet). On desktop, Back must keep leaving the page. Read
  // through a ref so a resize across the breakpoint never re-runs the effect
  // and calls showModal() on a dialog that is already open.
  const adaptive = useAdaptive();
  const adaptiveRef = useRef(adaptive);
  adaptiveRef.current = adaptive;
  const id = useId();
  // Set once the overlay is being left for another screen: its entry, if it has not been pushed
  // yet, must never be pushed now, or it would land on top of the navigation.
  const departed = useRef(false);
  // A retained overlay closed through `open` and opened again is a new opening, which gets its
  // entry. Reset on the close, not in the layout effect, so a replay within one opening keeps it.
  useEffect(() => {
    if (!open) departed.current = false;
  }, [open]);
  const leave = useCallback(
    (then: () => void) => {
      departed.current = true;
      const go = () => {
        if (window.history.state?.__samOverlay === id) {
          window.addEventListener('popstate', () => queueMicrotask(then), { once: true });
          window.history.back();
        } else then();
      };
      // Another overlay's Back may still be travelling (this one opened as it closed, or this
      // one's effect re-ran). Navigating before it lands lets it undo the navigation, and reading
      // the entry before it lands can find an entry that Back is already removing.
      if (pendingBack) void pendingBack.then(go);
      else go();
    },
    [id],
  );
  useLayoutEffect(() => {
    if (!open || !ref.current) return;
    const dialog = ref.current;
    const previous =
      pointerInvoker?.node.isConnected && performance.now() - pointerInvoker.at < 1000
        ? pointerInvoker.node
        : document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;
    const lock = Symbol(id);
    if (scrollLocks.size === 0) bodyOverflow = document.body.style.overflow;
    scrollLocks.add(lock);
    document.body.style.overflow = 'hidden';
    if (dialog.showModal) dialog.showModal();
    else dialog.setAttribute('open', '');
    const enhanceContent = () => {
      for (const button of dialog.querySelectorAll<HTMLButtonElement>(
        '.drawer-hd button,.modal-x',
      )) {
        if (button.textContent?.trim() === '×' && !button.hasAttribute('aria-label'))
          button.setAttribute('aria-label', 'Close dialog');
      }
      // Read-only details and bounded tables/code must also scroll by keyboard,
      // including content that arrives after the dialog's initial render.
      for (const region of dialog.querySelectorAll<HTMLElement>('.drawer-body,.tbl-wrap,pre')) {
        if (!region.hasAttribute('tabindex')) region.tabIndex = 0;
        if (!region.hasAttribute('aria-label'))
          region.setAttribute(
            'aria-label',
            region.matches('pre')
              ? 'Code'
              : region.matches('.tbl-wrap')
                ? 'Detail table'
                : `${label} content`,
          );
      }
    };
    enhanceContent();
    const contentObserver = new MutationObserver(enhanceContent);
    contentObserver.observe(dialog, { childList: true, subtree: true });
    // A long detail/form starts at its title, keeping the opening context in
    // view instead of jumping to a footer action. Tab then reaches controls.
    if (kind === 'detail' || kind === 'form') {
      const title = dialog.querySelector<HTMLElement>('.drawer-hd h2,.modal-title,h2,h1');
      if (title) {
        title.tabIndex = -1;
        title.dataset.overlayTitle = 'true';
        title.focus({ preventScroll: true });
      }
    }
    // StrictMode replays effects. Register only the surviving mount, otherwise
    // its cleanup's asynchronous history.back() would dismiss the real dialog.
    let alive = true;
    let registered = false;
    const url = window.location.href;
    queueMicrotask(() => {
      if (!alive || !adaptiveRef.current) return;
      void (pendingBack ?? Promise.resolve()).then(() => {
        if (!alive || !adaptiveRef.current || departed.current) return;
        registered = true;
        stack.push(id);
        window.history.pushState({ ...window.history.state, __samOverlay: id }, '');
      });
    });
    const keep = () => window.history.pushState({ ...window.history.state, __samOverlay: id }, '');
    const back = () => {
      if (stack.at(-1) !== id || window.history.state?.__samOverlay === id) return;
      // `onClose` returning `false` is a refusal, not a close: the overlay has
      // unsaved work and is showing its own discard prompt instead. The entry
      // this Back consumed has to go back on the stack, or the overlay is left
      // on screen with nothing between it and the page behind — the next Back
      // would leave the route with the edit still unsaved.
      if (!dismissRef.current || closeRef.current() === false) keep();
    };
    window.addEventListener('popstate', back);
    return () => {
      alive = false;
      contentObserver.disconnect();
      window.removeEventListener('popstate', back);
      const index = stack.indexOf(id);
      if (index >= 0) stack.splice(index, 1);
      if (dialog.close) dialog.close();
      else dialog.removeAttribute('open');
      scrollLocks.delete(lock);
      if (scrollLocks.size === 0) document.body.style.overflow = bodyOverflow;
      if (registered && window.location.href === url && window.history.state?.__samOverlay === id) {
        let settle: () => void = () => {};
        const settled = new Promise<void>((resolve) => {
          settle = resolve;
        });
        const done = () => {
          window.removeEventListener('popstate', done);
          clearTimeout(timer);
          if (pendingBack === settled) pendingBack = null;
          settle();
        };
        // A browser that swallows the traversal must not park the next overlay forever.
        const timer = setTimeout(done, 1000);
        window.addEventListener('popstate', done);
        pendingBack = settled;
        window.history.back();
      }
      // Return focus to the invoker; when it is gone (a virtualized row that
      // scrolled out, a list that re-rendered) land on the page title or the
      // main region rather than <body>, so keyboard users keep their place.
      queueMicrotask(() => {
        if (previous?.isConnected) {
          previous.focus({ preventScroll: true });
          return;
        }
        const fallback = document.querySelector<HTMLElement>(
          '[data-overlay-return], .page-title, .main',
        );
        if (!fallback) return;
        if (!fallback.hasAttribute('tabindex')) fallback.tabIndex = -1;
        fallback.focus({ preventScroll: true });
      });
    };
  }, [open, id, kind]);
  useEffect(() => {
    if (!open) return;
    const viewport = window.visualViewport;
    const resize = () => {
      if (!ref.current || !viewport) return;
      ref.current.style.setProperty('--overlay-height', `${viewport.height}px`);
      ref.current.style.setProperty('--overlay-top', `${viewport.offsetTop}px`);
    };
    resize();
    viewport?.addEventListener('resize', resize);
    viewport?.addEventListener('scroll', resize);
    return () => {
      viewport?.removeEventListener('resize', resize);
      viewport?.removeEventListener('scroll', resize);
    };
  }, [open]);
  if (!open) return null;
  return (
    <dialog
      ref={ref}
      className={`responsive-overlay ${className}`}
      data-kind={kind}
      aria-label={label}
      style={style}
      onClickCapture={(event) => {
        const anchor = (event.target as HTMLElement).closest?.(
          'a[href]',
        ) as HTMLAnchorElement | null;
        // In-page anchors (`#section`) stay inside the overlay; only real
        // same-origin navigations need the overlay's history entry removed first.
        if (
          !router ||
          !anchor ||
          anchor.target === '_blank' ||
          anchor.hasAttribute('download') ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey ||
          anchor.origin !== window.location.origin ||
          anchor.getAttribute('href')?.startsWith('#')
        )
          return;
        // Remove the overlay's history entry BEFORE in-app navigation. Otherwise
        // cleanup's asynchronous Back can cancel the destination navigation.
        event.preventDefault();
        event.stopPropagation();
        const destination = anchor.pathname + anchor.search + anchor.hash;
        leave(() => {
          closeRef.current();
          void router.navigate({ to: destination });
        });
      }}
      onCancel={(event) => {
        event.preventDefault();
        if (dismissible) onClose();
      }}
      onClick={(event) => {
        if (event.target !== ref.current || !dismissible) return;
        const rect = ref.current.getBoundingClientRect();
        if (
          event.clientX < rect.left ||
          event.clientX > rect.right ||
          event.clientY < rect.top ||
          event.clientY > rect.bottom
        )
          onClose();
      }}
    >
      <OverlayLeaveContext.Provider value={leave}>{children}</OverlayLeaveContext.Provider>
    </dialog>
  );
}
